/**
 * Pure string helpers. No locale assumptions beyond what's documented.
 */

export const slugify = (input: string): string =>
  input
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strip diacritics
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

export const truncate = (input: string, max: number, suffix = '...'): string =>
  input.length <= max ? input : input.slice(0, max - suffix.length) + suffix;

export const initials = (fullName: string, max = 2): string =>
  fullName
    .trim()
    .split(/\s+/)
    .slice(0, max)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

export const maskEmail = (email: string): string => {
  const [local, domain] = email.split('@');
  if (!local || !domain) return email;
  const visible = local.slice(0, 2);
  return `${visible}${'*'.repeat(Math.max(local.length - 2, 1))}@${domain}`;
};

/** A real HTML tag (`<p>`, `</li>`, `<br/>`), not a stray `<` in prose ("age < 5"). */
const HTML_TAG = /<\/?[a-z][a-z0-9]*(?:\s[^>]*)?\/?>/i;

const escapeHtml = (input: string): string =>
  input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * Normalises a description that may hold either rich-text editor HTML or
 * legacy plain text (written before the field got an editor, or imported).
 * HTML passes through untouched; plain text is escaped, blank-line blocks
 * become `<p>` and single newlines `<br>`. Empty input returns ''.
 */
export const textToHtml = (value: string | null | undefined): string => {
  const text = value?.trim() ?? '';
  if (!text) return '';
  if (HTML_TAG.test(text)) return text;
  return text
    .split(/\r?\n\s*\r?\n/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block) => `<p>${escapeHtml(block).replace(/\r?\n/g, '<br>')}</p>`)
    .join('');
};
